import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { setupAdminUserProfile, checkAdminStatus } from "../utils/adminSetup";
import toast from "react-hot-toast";
import {
  CogIcon,
  UserIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

const Settings: React.FC = () => {
  const { user, refreshUserData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [adminStatus, setAdminStatus] = useState<{
    isAdmin: boolean;
    error?: string;
  }>({ isAdmin: false });

  useEffect(() => {
    checkCurrentAdminStatus();
  }, [user]);

  const checkCurrentAdminStatus = async () => {
    if (!user) {
      return;
    }

    const result = await checkAdminStatus();
    setAdminStatus(result);
  };

  const handleSetupAdmin = async () => {
    if (!user) {
      toast.error("Please log in first");
      return;
    }

    setLoading(true);
    try {
      const result = await setupAdminUserProfile(user.email);

      if (result.success) {
        toast.success(result.message || "Admin setup completed successfully");
        await refreshUserData();
        await checkCurrentAdminStatus();
      } else {
        toast.error(result.error || "Failed to set up admin");
      }
    } catch (error) {
      console.error("Error setting up admin:", error);
      toast.error("Unexpected error during admin setup");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">
          Application settings and user preferences
        </p>
      </div>

      <div className="space-y-6">
        {/* Admin Setup Section */}
        {user && user.email === "joeri@met.care" && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <UserIcon className="h-6 w-6 text-primary-600 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">
                Administrator Setup
              </h2>
            </div>

            {adminStatus.isAdmin ? (
              <div className="flex items-center p-4 bg-green-50 rounded-md">
                <CheckCircleIcon className="h-5 w-5 text-green-400 mr-3" />
                <div>
                  <p className="text-sm font-medium text-green-800">
                    Admin Access Configured
                  </p>
                  <p className="text-sm text-green-700">
                    You have administrator privileges and can manage users.
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-start p-4 bg-yellow-50 rounded-md mb-4">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">
                      Admin Setup Required
                    </p>
                    <p className="text-sm text-yellow-700">
                      Set up administrator access to manage users and system
                      settings.
                    </p>
                  </div>
                </div>

                <div className="text-sm text-gray-600 mb-4">
                  <p>
                    <strong>Current Email:</strong> {user.email}
                  </p>
                  <p>
                    <strong>Current Role:</strong> {user.role}
                  </p>
                </div>

                <button
                  onClick={handleSetupAdmin}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Setting up...
                    </>
                  ) : (
                    <>
                      <CogIcon className="h-4 w-4 mr-2" />
                      Set Up Admin Access
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* General Settings Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="mx-auto h-12 w-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg
              className="h-6 w-6 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Settings & Configuration
          </h3>
          <p className="text-gray-600 mb-4">
            This module will allow you to configure user preferences,
            notification settings, pharmacy details, and system configurations.
          </p>
          <p className="text-sm text-gray-500">
            Coming soon - This feature is under development
          </p>
        </div>
      </div>
    </div>
  );
};

export default Settings;
